<template>
	<div>
		<v-main>
			<v-container fluid fill-height>
				<v-layout justify-center align-center>
					<v-card width="100%" outlined elevation-0>
						<v-toolbar flat class="border_bottom" shaped>
							<v-btn v-if="ret" icon data-tour="back" :title="ret.label" @click="back">
								<v-icon>mdi-arrow-left</v-icon>
							</v-btn>
							<v-toolbar-title>Analisi prezzi eBay</v-toolbar-title>
							<v-btn class="ml-6" outlined color="primary" data-tour="tutorial" @click="tour.start()">
								<v-icon left>mdi-school-outline</v-icon>
								Tutorial guidato
							</v-btn>
							<v-spacer></v-spacer>
							<v-btn text :disabled="!marketplace" :loading="loading" @click="refresh">
								<v-icon left>mdi-refresh</v-icon>
								Ricarica
							</v-btn>
							<v-btn text color="primary" data-tour="scan" :disabled="!marketplace || jobRunning" :loading="sending === 'scan'" @click="startJob('scan')">
								<v-icon left>mdi-cloud-download-outline</v-icon>
								Aggiorna elenco
							</v-btn>
							<v-btn color="primary" data-tour="analysis" :disabled="!marketplace || jobRunning || followedTotal === 0" :loading="sending === 'analysis'" @click="openAnalysisConfirm">
								<v-icon left>mdi-account-search-outline</v-icon>
								Ricerca concorrenti
							</v-btn>
							<template v-slot:extension>
								<MarketplaceTabs platform="ebay" />
							</template>
						</v-toolbar>


						<v-col cols="12">
							<v-row>
								<v-col cols="12" md="3" data-tour="country">
									<v-select
										v-model="marketplace"
										:items="marketplaceOptions"
										:loading="loadingMarketplaces"
										label="Paese"
										item-text="text"
										item-value="value"
										outlined
										dense
										hide-details
										@change="changeMarketplace"
									></v-select>
								</v-col>
								<v-col cols="12" md="3">
									<v-text-field
										v-model="searchfield"
										label="Cerca SellerSku, ItemID, EAN o titolo"
										outlined
										append-icon="mdi-magnify"
										dense
										hide-details
										clearable
										@click:append="applySearch"
										@keyup.enter="applySearch"
										@click:clear="clearSearch"
									></v-text-field>
								</v-col>
								<v-col cols="12" md="6" class="d-flex align-center flex-wrap" data-tour="filters">
									<v-switch v-model="onlyFollowed" color="primary" inset hide-details class="mt-0 mr-6" :label="`Solo seguiti (${followedTotal})`" @change="applyFilters"></v-switch>
									<v-select
										v-model="compare"
										:items="compareOptions"
										label="Confronto"
										outlined
										dense
										hide-details
										style="max-width: 220px"
										@change="applyFilters"
									></v-select>
								</v-col>
							</v-row>

							<v-alert v-if="lastError" type="error" text dense class="mt-4 mb-0">{{ lastError }}</v-alert>
							<!-- analisi: lavoro in coda di minuti, si mostra l'avanzamento e la fine prevista -->
							<v-alert v-if="analysisRunning" type="info" text dense class="mt-4 mb-0" data-tour="progress">
								<template v-if="jobs.analysis.status === 'queued'">
									Ricerca concorrenti di {{ jobs.analysis.total || '' }} prodotti in coda dalle {{ jobs.analysis.created_at | moment('HH:mm') }}: partirà appena la coda è libera
									(durata stimata {{ duration(jobs.analysis.estimated_ms) }}).
								</template>
								<template v-else>
									Ricerca concorrenti in corso: {{ jobs.analysis.processed || 0 }} / {{ jobs.analysis.total || 0 }} prodotti ({{ progress }}%)
									<span v-if="expectedEnd"> — fine prevista verso le {{ expectedEnd | moment('HH:mm') }}</span>.
									<v-progress-linear :value="progress" color="primary" height="6" rounded class="mt-2"></v-progress-linear>
								</template>
								<small class="d-block mt-1">Puoi chiudere la pagina: l'analisi prosegue e riceverai una notifica alla fine.</small>
							</v-alert>
							<v-alert v-else-if="jobRunning" type="info" text dense class="mt-4 mb-0">
								Aggiornamento dell'elenco in corso (lo stesso di Associa prodotti): il report di eBay può richiedere qualche minuto. Riceverai una notifica quando è pronto.
								<br />
								<small>Richiesta delle {{ runningJob.created_at | moment('HH:mm') }} — {{ runningJob.status === 'queued' ? 'in attesa di essere presa in carico' : 'in lavorazione' }}.</small>
							</v-alert>
							<v-alert v-else-if="failedJob" type="error" text dense class="mt-4 mb-0">
								{{ failedJob === jobs.analysis ? "L'ultima ricerca concorrenti" : "L'ultimo aggiornamento dell'elenco" }} non è riuscita: {{ failedJob.error }}
							</v-alert>
							<v-alert v-else-if="jobs.scan" type="success" text dense class="mt-4 mb-0">
								Elenco aggiornato il {{ jobs.scan.updated_at | moment('DD/MM/YYYY HH:mm') }}
								<span v-if="scanCounters"> — {{ scanCounters.active }} inserzioni attive, {{ scanCounters.notAssociated }} non associate</span>
								<span v-else> — elenco condiviso con Associa prodotti: premi <strong>Aggiorna elenco</strong> per aggiungere anche le inserzioni non associate</span>.
								<span v-if="jobs.analysis && jobs.analysis.status === 'done'"> Ultima ricerca concorrenti il {{ jobs.analysis.updated_at | moment('DD/MM/YYYY HH:mm') }}<span v-if="jobs.analysis.error === 'quota_ebay'"> (interrotta per il limite giornaliero di eBay)</span>.</span>
							</v-alert>
							<v-alert v-else type="info" text dense class="mt-4 mb-0">
								Qui trovi le inserzioni eBay associate ai tuoi prodotti. Premi <strong>Aggiorna elenco</strong> (lo stesso di Associa prodotti) per aggiungere anche quelle non associate.
							</v-alert>
						</v-col>

						<PriceTable
							:items="items"
							:total="total"
							:options.sync="options"
							:loading="loading"
							:mobile-cards="mobileCards"
							:following-id="followingId"
							:analyzing-id="analyzingId"
							:analyze-disabled="jobRunning"
							@follow="toggleFollow"
							@adjust="openAdjust"
							@analyze="analyzeOne"
						></PriceTable>
					</v-card>

					<v-snackbar v-model="snackbar.snackbar" :color="snackbar.color" :timeout="snackbar.timeout">
						{{ snackbar.text }}
						<v-btn dark text @click="snackbar.snackbar = false">Close</v-btn>
					</v-snackbar>
				</v-layout>
			</v-container>
		</v-main>

		<AdjustPriceDialog v-model="adjustDialog" :item="current" :saving="saving" :error="adjustError" @confirm="confirmAdjust"></AdjustPriceDialog>
		<AnalysisConfirmDialog
			v-model="analysisDialog"
			:loading="loadingEstimate"
			:estimate="estimate"
			title="Ricerca concorrenti"
			intro="per ognuno si cercano su eBay le inserzioni con lo stesso EAN e si confrontano i totali (prezzo + spedizione)."
			pace="Si fa una ricerca per prodotto e il limite giornaliero di eBay è condiviso."
			@confirm="confirmAnalysis"
		></AnalysisConfirmDialog>

		<!-- Ultimo elemento: il nodo viene spostato in `[data-app]` al montaggio. -->
		<TourOverlay :tour="tour"></TourOverlay>
	</div>
</template>

<script>
import { regions as ebayRegions } from '@/utils/ebay/marketplace';
import TourOverlay from '@/components/core/tour/TourOverlay.vue';
import MarketplaceTabs from '@/components/marketplace/MarketplaceTabs';
import { useTour } from '@/components/core/tour/useTour';
import { createPriceAnalysisTourSteps } from './price-analysis/price-analysis-tour';
import PriceTable from './price-analysis/PriceTable.vue';
import { priceAnalysisRouteState } from '@/components/marketplace/priceAnalysisRouteState';
import { returnInfo, goBackWithResult } from '@/components/core/pageReturn';
import AdjustPriceDialog from './price-analysis/AdjustPriceDialog.vue';
import AnalysisConfirmDialog from '@/views/amazon/price-analysis/AnalysisConfirmDialog.vue';

// oltre questa soglia una scansione "in corso" e' appesa: il bottone torna premibile.
// Per l'analisi decide il server, che conosce la durata stimata.
const RUNNING_MAX_MS = 45 * 60 * 1000;
// durante l'analisi si ricontrolla piu' spesso: le righe si aggiornano man mano
const POLL_ANALYSIS_MS = 10000;
const POLL_SCAN_MS = 15000;

// Inserzioni eBay (associate o no), prodotti seguiti, concorrenti per EAN e aggiustamento prezzo.
// Stessa struttura della pagina Amazon: su eBay non c'e' la buybox, si confrontano i totali.
export default {
	name: 'EbayPriceAnalysis',
	components: { TourOverlay, PriceTable, AdjustPriceDialog, AnalysisConfirmDialog, MarketplaceTabs },
	// paese, ricerca, filtri e pagina nell'URL: refresh e ritorno ripartono da li'
	mixins: [priceAnalysisRouteState('compare')],
	// il controller del tutorial nasce qui e non in data(): vedi Unmatched.vue
	setup() {
		return {
			tour: useTour(createPriceAnalysisTourSteps),
		};
	},
	// l'evento arriva nella stanza del profilo a fine scansione/analisi
	sockets: {
		MESSAGE_EBAY_PRICE(raw) {
			let payload = {};
			try {
				payload = typeof raw === 'string' ? JSON.parse(raw) : raw || {};
			} catch (e) {
				payload = {};
			}
			if (payload.marketplaceId && payload.marketplaceId !== this.marketplace) {
				return;
			}
			this.refresh();
		},
	},
	data() {
		return {
			// aperta da un'altra pagina (es. consigli della scheda prodotto): la freccia riporta li'
			ret: returnInfo(this.$route),
			marketplace: null,
			activeMarketplaces: [],
			loadingMarketplaces: false,
			loading: false,
			sending: '',
			searchfield: '',
			search: '',
			onlyFollowed: false,
			compare: '',
			compareOptions: [
				{ text: 'Tutte', value: '' },
				{ text: 'Siamo i più economici', value: 'cheapest' },
				{ text: 'Più cari di un concorrente', value: 'beaten' },
				{ text: 'Nessun concorrente', value: 'none' },
				{ text: 'Senza EAN', value: 'no_ean' },
			],
			items: [],
			total: 0,
			followedTotal: 0,
			jobs: { scan: null, analysis: null },
			lastError: '',
			pollTimer: null,
			followingId: '',
			// riga su cui gira "Analizza ora"
			analyzingId: '',
			analysisDialog: false,
			loadingEstimate: false,
			estimate: null,
			adjustDialog: false,
			current: null,
			saving: false,
			adjustError: '',
			options: {
				page: 1,
				itemsPerPage: 25,
			},
			snackbar: {
				snackbar: false,
				color: '',
				timeout: 6000,
				text: '',
			},
		};
	},
	computed: {
		mobileCards() {
			return this.$vuetify.breakpoint.width < 769;
		},
		runningJob() {
			const inProgress = job => job && (job.status === 'queued' || job.status === 'running');
			if (inProgress(this.jobs.analysis)) {
				return this.jobs.analysis;
			}
			const scan = this.jobs.scan;
			return inProgress(scan) && Date.now() - new Date(scan.created_at).getTime() < RUNNING_MAX_MS ? scan : null;
		},
		analysisRunning() {
			return !!this.runningJob && this.runningJob === this.jobs.analysis;
		},
		progress() {
			const job = this.jobs.analysis;
			if (!job || !job.total) {
				return 0;
			}
			return Math.min(100, Math.round(((job.processed || 0) / job.total) * 100));
		},
		// dal ritmo reale se ci sono gia' progressi, altrimenti dalla stima
		expectedEnd() {
			const job = this.jobs.analysis;
			if (!job || !job.started_at) {
				return null;
			}
			const started = new Date(job.started_at).getTime();
			const processed = job.processed || 0;
			if (processed > 0 && job.total) {
				const elapsed = Date.now() - started;
				return new Date(started + (elapsed / processed) * job.total);
			}
			return job.estimated_ms ? new Date(started + job.estimated_ms) : null;
		},
		jobRunning() {
			return !!this.runningJob;
		},
		// contatori scritti dalla scansione condivisa con "Associa prodotti"
		scanCounters() {
			const counters = this.jobs.scan && this.jobs.scan.counters && this.jobs.scan.counters.priceAnalysis;
			return counters && !counters.error ? counters : null;
		},
		// si mostra l'errore solo se e' il job piu' recente: uno vecchio non interessa piu'
		failedJob() {
			const latest = [this.jobs.scan, this.jobs.analysis].filter(Boolean).sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
			return latest && latest.status === 'failed' ? latest : null;
		},
		fallbackMarketplaces() {
			return ebayRegions.filter(region => region.MarketplaceId && region.marketplace).map(region => ({ text: region.marketplace, value: region.MarketplaceId }));
		},
		marketplaceOptions() {
			if (this.activeMarketplaces.length === 0) {
				return this.fallbackMarketplaces;
			}
			return this.activeMarketplaces.map(item => {
				const known = this.fallbackMarketplaces.find(option => option.value === item.marketplaceId);
				return {
					text: `${known ? known.text : item.marketplaceId} (${item.total})`,
					value: item.marketplaceId,
				};
			});
		},
	},
	watch: {
		options: {
			handler: function () {
				this.getDataFromApi();
			},
			deep: true,
		},
	},
	async created() {
		this.leggiStatoUrl();
		await this.loadMarketplaces();
	},
	beforeDestroy() {
		this.tour.stop();
		this.stopPolling();
	},
	methods: {
		back() {
			if (this.ret) return goBackWithResult(this.$router, this.ret, '', null);
			this.$router.push({ name: 'ebay' });
		},
		async loadMarketplaces() {
			this.loadingMarketplaces = true;
			const res = await this.$store.dispatch('marketplaceCatalog/marketplaces', { platform: 'ebay' });
			this.loadingMarketplaces = false;
			this.activeMarketplaces = res && !res.error && Array.isArray(res.marketplaces) ? res.marketplaces : [];

			if (this.marketplace && !this.marketplaceOptions.some(o => o.value === this.marketplace)) {
				this.marketplace = null;
			}
			if (this.marketplace) {
				await this.refresh();
			} else if (this.activeMarketplaces.length > 0) {
				this.marketplace = this.marketplaceOptions[0].value;
				await this.changeMarketplace();
			}
		},
		async changeMarketplace() {
			this.options.page = 1;
			this.items = [];
			this.total = 0;
			this.lastError = '';
			await this.loadJobs();
			await this.getDataFromApi();
		},
		async refresh() {
			await this.loadJobs();
			await this.getDataFromApi();
		},
		applySearch() {
			this.search = this.searchfield || '';
			this.applyFilters();
		},
		clearSearch() {
			this.searchfield = '';
			this.applySearch();
		},
		applyFilters() {
			if (this.options.page !== 1) {
				// il watcher su options ricarica da solo
				this.options.page = 1;
				return;
			}
			this.getDataFromApi();
		},
		async loadJobs() {
			if (!this.marketplace) {
				return;
			}
			const res = await this.$store.dispatch('ebayPrice/job', { marketplace: this.marketplace });
			this.jobs = res && !res.error ? { scan: res.scan, analysis: res.analysis } : { scan: null, analysis: null };

			if (this.jobRunning) {
				this.startPolling();
			} else {
				this.stopPolling();
			}
		},
		// il socket avvisa a fine lavoro; il polling copre il caso in cui l'evento vada perso
		startPolling() {
			if (this.pollTimer) {
				return;
			}
			const analysis = this.analysisRunning;
			this.pollTimer = setInterval(async () => {
				const wasRunning = this.jobRunning;
				await this.loadJobs();
				// durante l'analisi le righe si aggiornano man mano; a fine lavoro si ricarica comunque
				if (analysis || (wasRunning && !this.jobRunning)) {
					await this.getDataFromApi();
				}
				// cambiato il tipo di lavoro (fine analisi, inizio scansione): ritmo nuovo
				if (this.jobRunning && this.analysisRunning !== analysis) {
					this.stopPolling();
					this.startPolling();
				}
			}, analysis ? POLL_ANALYSIS_MS : POLL_SCAN_MS);
		},
		stopPolling() {
			if (this.pollTimer) {
				clearInterval(this.pollTimer);
				this.pollTimer = null;
			}
		},
		async getDataFromApi() {
			if (!this.marketplace) {
				return;
			}
			this.loading = true;
			const res = await this.$store.dispatch('ebayPrice/rows', {
				marketplace: this.marketplace,
				page: this.options.page,
				limit: this.options.itemsPerPage,
				search: this.search,
				followed: this.onlyFollowed,
				compare: this.compare,
			});
			this.loading = false;
			this.scriviStatoUrl();

			if (res && res.error) {
				this.items = [];
				this.total = 0;
				this.lastError = this.errorText(res);
				return;
			}
			this.items = res.items || [];
			this.total = res.total || 0;
			this.ripristinaPaginaUrl();
			this.followedTotal = res.followedTotal || 0;
		},
		// prima di lanciare si dice quanti prodotti e quanto tempo: non e' un'operazione in tempo reale
		async openAnalysisConfirm() {
			this.estimate = null;
			this.analysisDialog = true;
			this.loadingEstimate = true;
			const res = await this.$store.dispatch('ebayPrice/analysisEstimate', { marketplace: this.marketplace });
			this.loadingEstimate = false;
			this.estimate = res && !res.error ? res : null;
		},
		async confirmAnalysis() {
			this.analysisDialog = false;
			await this.startJob('analysis');
		},
		duration(ms) {
			const minutes = Math.ceil((Number(ms) || 0) / 60000);
			return minutes <= 1 ? 'circa un minuto' : `circa ${minutes} minuti`;
		},
		async startJob(type) {
			this.sending = type;
			// l'elenco lo aggiorna la stessa scansione di "Associa prodotti": un solo download per le due pagine
			const res =
				type === 'analysis'
					? await this.$store.dispatch('ebayPrice/startAnalysis', { marketplace: this.marketplace })
					: await this.$store.dispatch('marketplaceUnmatched/scan', { platform: 'ebay', marketplace: this.marketplace });
			this.sending = '';

			if (res && res.error) {
				this.lastError = this.errorText(res);
				this.notify(this.lastError, 'error');
				await this.loadJobs();
				return;
			}
			this.lastError = '';
			this.notify(type === 'analysis' ? "Ricerca concorrenti messa in coda: segui l'avanzamento qui sopra, riceverai una notifica al termine." : 'Elenco in preparazione: può richiedere qualche minuto, riceverai una notifica quando è pronto.', 'info');
			await this.refresh();
		},
		async toggleFollow(item) {
			this.followingId = item._id;
			const res = await this.$store.dispatch('ebayPrice/follow', { id: item._id, followed: !item.followed });
			this.followingId = '';

			if (res && res.error) {
				this.notify(this.errorText(res), 'error');
				return;
			}
			if (res.row && res.row.removed) {
				// non piu' attiva e non piu' seguita: non ha motivo di restare in elenco
				this.items = this.items.filter(row => row._id !== item._id);
				this.total = Math.max(0, this.total - 1);
			} else {
				item.followed = res.row.followed;
			}
			this.followedTotal = Math.max(0, this.followedTotal + (res.row.followed ? 1 : -1));
		},
		// "Analizza ora": ricerca per EAN della sola inserzione, senza aspettare il job
		async analyzeOne(item) {
			this.analyzingId = item._id;
			const res = await this.$store.dispatch('ebayPrice/analyzeOne', { id: item._id });
			this.analyzingId = '';
			if (res && res.error) {
				this.notify(this.analyzeErrorText(res), 'error');
				return;
			}
			const index = this.items.findIndex(row => row._id === item._id);
			if (index !== -1 && res.row) this.$set(this.items, index, res.row);
			const a = (res.row && res.row.analysis) || {};
			const euro = n => `${(+n || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${a.currency || 'EUR'}`;
			if (a.status === 'done' && a.isCheapest) this.notify(`Sei il più economico a ${euro(a.ourTotal)} (${a.offers || 0} offerte con lo stesso EAN).`, 'success');
			else if (a.status === 'done') this.notify(`Sei ${a.position ? `${a.position}° su ${a.offers}` : 'dietro'}: ${a.competitorSeller || 'un venditore'} a ${euro(a.competitorTotal)}, tu a ${euro(a.ourTotal)}.`, 'warning');
			else if (a.status === 'no_competitors') this.notify('Nessun altro venditore con questo EAN.', 'success');
			else this.notify(`eBay non ha risposto per questa inserzione${a.error ? `: ${a.error}` : ''}.`, 'error');
		},
		analyzeErrorText(res) {
			const error = String(res.error || '');
			if (error === 'job_running') return "È in corso l'analisi completa di questo paese: l'inserzione verrà analizzata lì.";
			if (error === 'busy') return "Un'altra analisi singola è in corso: riprova fra qualche secondo.";
			if (error === 'quota') return 'eBay chiede di rallentare: riprova fra qualche secondo.';
			if (error === 'no_ean') return 'Questa inserzione non ha l’EAN: su eBay non si può confrontare.';
			return this.errorText(res);
		},
		openAdjust(item) {
			this.current = item;
			this.adjustError = '';
			this.adjustDialog = true;
		},
		async confirmAdjust(price) {
			this.saving = true;
			this.adjustError = '';
			const res = await this.$store.dispatch('ebayPrice/adjustPrice', { id: this.current._id, price });
			this.saving = false;

			if (res && res.error) {
				this.adjustError = this.errorText(res);
				return;
			}
			const index = this.items.findIndex(row => row._id === this.current._id);
			if (index !== -1 && res.row) {
				this.$set(this.items, index, res.row);
			}
			this.adjustDialog = false;
			const done = res.associated ? 'eBay ha accettato il nuovo prezzo, salvato anche sul prodotto.' : 'eBay ha accettato il nuovo prezzo.';
			// avvisi non bloccanti di eBay: la modifica e' passata, ma vanno letti
			const warnings = Array.isArray(res.warnings) && res.warnings.length ? ` Avvisi di eBay: ${res.warnings.join(' - ')}` : '';
			this.notify(done + warnings, warnings ? 'warning' : 'success');
		},
		errorText(res) {
			const error = String(res.error || '');
			if (res.status === 409) {
				return 'Un lavoro per questo paese è già in corso: attendi che finisca.';
			}
			if (res.status === 404 && !error) {
				return 'Questa sezione non è disponibile su questo server: il backend non è aggiornato.';
			}
			if (error.indexOf('not_configured') !== -1) {
				return 'Le credenziali eBay non sono configurate per questo profilo.';
			}
			if (error.indexOf('no_followed_products') !== -1) {
				return "Nessun prodotto seguito con EAN: metti la stellina sui prodotti da confrontare (serve l'EAN).";
			}
			// il rifiuto di eBay (o la modifica non disponibile) arriva gia' spiegato
			if (error.indexOf('eBay ha rifiutato') === 0 || error.indexOf('Modifica non disponibile') === 0) {
				return error;
			}
			return `Errore durante la richiesta${error ? `: ${error}` : ''}.`;
		},
		notify(text, color) {
			this.snackbar.color = color;
			this.snackbar.text = text;
			this.snackbar.snackbar = true;
		},
	},
};
</script>
